// TAB1 (DEC-270, design/mockups/core-tab-search-notes.md): the pure matcher
// behind Open Tabs' find row. No DOM, no state, so a unit test can prove
// the rule directly.
//
// The rule: trim, lowercase, split the typed text on whitespace; EVERY word
// must be a substring of at least one of the tab's label, one of its guest
// labels, or its opener's name. Words may land in different fields (so
// "sam birthday" finds the tab named Birthday party whose guest is Samantha).
//
// Mine means the tab was opened by the signed-in user (DEC-270). A tab with
// no recorded opener never counts as Mine.

export interface SearchableTab {
  /** The label the card shows (typed name, else guests, else Walk-in). */
  label: string;
  guestLabels: readonly string[];
  openedByUserId: string | null;
  openedByName: string | null;
  /** TAB3: true when the tab's balance is zero, false when it is not, absent
   * while the figures have not loaded (it then matches neither status chip). */
  paid?: boolean | undefined;
  /** RCPT1-S3: true when the tab's card is authorized and a tip written on its receipt is still to be entered. Such a tab is not
   * Unpaid (nothing is owed at the location) and not Paid (it is not captured): it has its own Awaiting tip chip. */
  awaitingTip?: boolean | undefined;
  /** TAB3: minutes the tab has been open, rounded the way the card's aging
   * badge rounds. Absent means unknown (never Over 1 hr). */
  ageMinutes?: number | undefined;
}

/** TAB3: the age at which a tab counts as Over 1 hr, the aging badge's own. */
export const OVER_ONE_HOUR_MINUTES = 60;

/** TAB3: the chip row. status is a single select (tap again to clear);
 * over1hr is an independent toggle. */
export interface TabChipFilters {
  status: "unpaid" | "awaiting" | "paid" | undefined;
  over1hr: boolean;
}

export const NO_TAB_CHIPS: TabChipFilters = { status: undefined, over1hr: false };

export type TabFilterMode = "all" | "mine";

export interface TabMatch {
  /** Every typed word was found somewhere on the tab. */
  matches: boolean;
  /** At least one typed word is inside the visible label. */
  inLabel: boolean;
  /** The first guest label holding a typed word, when there is one. */
  guestLabel: string | undefined;
  /** At least one typed word is inside the opener's name. */
  inOpener: boolean;
}

export function tokenizeSearch(query: string): string[] {
  return query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter((token) => token.length > 0);
}

function includesAny(text: string, tokens: readonly string[]): boolean {
  const lower = text.toLowerCase();
  return tokens.some((token) => lower.includes(token));
}

export function matchTab(tab: SearchableTab, tokens: readonly string[]): TabMatch {
  if (tokens.length === 0) {
    return { matches: true, inLabel: false, guestLabel: undefined, inOpener: false };
  }
  const fields = [tab.label, ...tab.guestLabels, tab.openedByName ?? ""].map((field) =>
    field.toLowerCase(),
  );
  const matches = tokens.every((token) => fields.some((field) => field.includes(token)));
  if (!matches) {
    return { matches: false, inLabel: false, guestLabel: undefined, inOpener: false };
  }
  return {
    matches: true,
    inLabel: includesAny(tab.label, tokens),
    guestLabel: tab.guestLabels.find((guest) => includesAny(guest, tokens)),
    inOpener: tab.openedByName !== null && includesAny(tab.openedByName, tokens),
  };
}

export function isMine(tab: SearchableTab, signedInUserId: string | undefined): boolean {
  return (
    signedInUserId !== undefined &&
    tab.openedByUserId !== null &&
    tab.openedByUserId === signedInUserId
  );
}

export interface FilterResult {
  /** Tabs matching the text and both chips, under All. */
  allCount: number;
  /** The same, and opened by the signed-in user. */
  mineCount: number;
  /** TAB3: Unpaid tabs among the text, All/Mine and Over 1 hr matches. */
  unpaidCount: number;
  /** TAB3: Paid tabs among the same set. */
  paidCount: number;
  /** RCPT1-S3: Awaiting tip tabs among the same set. */
  awaitingCount: number;
  /** TAB3: Over 1 hr tabs among the text, All/Mine and status matches. */
  over1hrCount: number;
  /** For each input index, whether the card shows under the chosen mode. */
  visible: boolean[];
  matchByIndex: TabMatch[];
}

function isOverOneHour(tab: SearchableTab): boolean {
  return tab.ageMinutes !== undefined && tab.ageMinutes >= OVER_ONE_HOUR_MINUTES;
}

export function filterTabs(
  tabs: readonly SearchableTab[],
  query: string,
  mode: TabFilterMode,
  signedInUserId: string | undefined,
  chips: TabChipFilters = NO_TAB_CHIPS,
): FilterResult {
  const tokens = tokenizeSearch(query);
  let allCount = 0;
  let mineCount = 0;
  let unpaidCount = 0;
  let paidCount = 0;
  let awaitingCount = 0;
  let over1hrCount = 0;
  const visible: boolean[] = [];
  const matchByIndex: TabMatch[] = [];
  for (const tab of tabs) {
    const match = matchTab(tab, tokens);
    matchByIndex.push(match);
    const mine = isMine(tab, signedInUserId);
    const scoped = match.matches && (mode === "all" || mine);
    const awaiting = tab.awaitingTip === true;
    const statusOk =
      chips.status === undefined ||
      (chips.status === "awaiting"
        ? awaiting
        : chips.status === "unpaid"
          ? tab.paid === false && !awaiting
          : tab.paid === true && !awaiting);
    const ageOk = !chips.over1hr || isOverOneHour(tab);
    if (match.matches && statusOk && ageOk) {
      allCount += 1;
      if (mine) {
        mineCount += 1;
      }
    }
    // Each chip's count ignores its own chip and follows the other filters.
    if (scoped && ageOk) {
      if (awaiting) {
        awaitingCount += 1;
      } else if (tab.paid === false) {
        unpaidCount += 1;
      } else if (tab.paid === true) {
        paidCount += 1;
      }
    }
    if (scoped && statusOk && isOverOneHour(tab)) {
      over1hrCount += 1;
    }
    visible.push(scoped && statusOk && ageOk);
  }
  return {
    allCount,
    mineCount,
    unpaidCount,
    paidCount,
    awaitingCount,
    over1hrCount,
    visible,
    matchByIndex,
  };
}

export interface TextSegment {
  text: string;
  marked: boolean;
}

/** Splits `text` into plain and marked segments, marking every
 * case-insensitive occurrence of any token (overlaps merged). The caller
 * builds DOM nodes from the segments; tab text is never put through
 * innerHTML. */
export function highlightSegments(text: string, tokens: readonly string[]): TextSegment[] {
  if (tokens.length === 0 || text.length === 0) {
    return [{ text, marked: false }];
  }
  const lower = text.toLowerCase();
  if (lower.length !== text.length) {
    // A few characters change length when lowercased, which would skew
    // every index below; show the text unmarked rather than mis-marked.
    return [{ text, marked: false }];
  }
  const marked: boolean[] = new Array<boolean>(text.length).fill(false);
  for (const token of tokens) {
    let from = 0;
    for (;;) {
      const at = lower.indexOf(token, from);
      if (at === -1) {
        break;
      }
      for (let i = at; i < at + token.length; i += 1) {
        marked[i] = true;
      }
      from = at + 1;
    }
  }
  const segments: TextSegment[] = [];
  for (let i = 0; i < text.length; i += 1) {
    const isMarked = marked[i] === true;
    const last = segments[segments.length - 1];
    if (last !== undefined && last.marked === isMarked) {
      last.text += text[i];
    } else {
      segments.push({ text: text[i] ?? "", marked: isMarked });
    }
  }
  return segments;
}
